import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Upload, Check, X, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const err = (e, f) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : f);

function Row({ r, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(r.name);
  const save = async (patch) => {
    try { await api.put(`/admin/surgical-catalogs/item/${r.id}`, patch); setEdit(false); onSaved(); }
    catch (e) { toast.error(err(e, 'Save failed')); }
  };
  return (
    <li className={`flex items-center gap-2 px-3 py-2 border-b border-slate-100 text-[13px] ${r.is_active ? '' : 'opacity-50'}`} data-testid={`scm-row-${r.id}`}>
      {edit ? (
        <>
          <input value={name} onChange={(e) => setName(e.target.value)} className="flex-1 border border-slate-300 rounded px-2 py-1" data-testid={`scm-edit-input-${r.id}`} />
          <button type="button" onClick={() => save({ name })} className="text-emerald-700" data-testid={`scm-edit-save-${r.id}`}><Check size={15} /></button>
          <button type="button" onClick={() => { setEdit(false); setName(r.name); }} className="text-slate-400"><X size={15} /></button>
        </>
      ) : (
        <>
          <span className="flex-1">{r.name}{r.source === 'founder' && <span className="ml-2 text-[10px] font-bold text-teal-700">ADDED</span>}</span>
          <button type="button" onClick={() => setEdit(true)} className="text-slate-500" data-testid={`scm-edit-${r.id}`}><Pencil size={13} /></button>
          <button type="button" onClick={() => save({ is_active: !r.is_active })} className="text-[11px] font-semibold text-slate-600 border border-slate-300 rounded px-2 py-0.5" data-testid={`scm-toggle-${r.id}`}>
            {r.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </>
      )}
    </li>
  );
}

export default function SurgicalCatalogsTab() {
  const [specs, setSpecs] = useState([]);
  const [sel, setSel] = useState('');
  const [rows, setRows] = useState(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const loadSpecs = useCallback(async () => {
    const { data } = await api.get('/admin/surgical-catalogs');
    setSpecs(data.specialties);
    setSel((s) => s || data.specialties[0]?.specialty || '');
  }, []);
  const loadRows = useCallback(async () => {
    if (!sel) return;
    const { data } = await api.get(`/admin/surgical-catalogs/${sel}`);
    setRows(data.rows);
  }, [sel]);
  useEffect(() => { loadSpecs().catch(() => toast.error('Could not load catalogs')); }, [loadSpecs]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const add = async () => {
    if (!newName.trim()) return;
    try { await api.post(`/admin/surgical-catalogs/${sel}`, { name: newName.trim() }); setNewName(''); loadRows(); loadSpecs(); }
    catch (e) { toast.error(err(e, 'Add failed')); }
  };
  const push = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/admin/surgical-catalogs/${sel}/push`); toast.success(`Pushed: ${data.renamed} renamed · ${data.hidden} hidden · ${data.added} added`); }
    catch (e) { toast.error(err(e, 'Push failed')); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="surgical-catalogs-tab">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Surgical Catalogs</h2>
        <p className="text-[13px] text-slate-500">Built-in operations per specialty. Doctors get their own specialty's list once; fixes reach them when you push.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {specs.map((s) => (
          <button key={s.specialty} type="button" onClick={() => { setSel(s.specialty); setRows(null); }}
            className={`px-3 py-1.5 rounded-full text-[12px] font-semibold border ${sel === s.specialty ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 text-slate-700'}`}
            data-testid={`scm-spec-${s.specialty}`}>
            {s.specialty} · {s.active} ops · {s.doctors_seeded} doctors
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={`Add operation to ${sel}`} className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-[13px]" data-testid="scm-add-input" />
        <button type="button" onClick={add} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-teal-700 text-white text-[13px] font-semibold" data-testid="scm-add-btn"><Plus size={14} /> Add</button>
        <button type="button" onClick={push} disabled={busy} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-900 text-slate-900 text-[13px] font-semibold disabled:opacity-50" data-testid="scm-push-btn">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Push fixes to doctors
        </button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl max-h-[60vh] overflow-y-auto">
        {!rows ? <div className="p-4 text-slate-400"><Loader2 size={16} className="animate-spin" /></div> : (
          <ul data-testid="scm-list">{rows.map((r) => <Row key={r.id} r={r} onSaved={() => { loadRows(); loadSpecs(); }} />)}</ul>
        )}
      </div>
    </div>
  );
}
